"use client";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { CalendarIcon, Plus, X } from "lucide-react";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useForm } from "react-hook-form";

import Input from "@/app/_module/components/FormInput";
import { Button } from "@/app/_module/components/ui/button";
import { Calendar } from "@/app/_module/components/ui/calendar";
import { Checkbox } from "@/app/_module/components/ui/checkbox";
import {
  Dialog,
  ResponsiveDialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/app/_module/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/app/_module/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/app/_module/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import { Textarea } from "@/app/_module/components/ui/textarea";
import { toast } from "@/app/_module/hooks/use-toast";
import { cn } from "@/app/_module/lib/utils";
import type { ReminderUI } from "@/app/_module/types/reminders";
import { LeadSelect } from "@/app/(authenticated)/appointments/_modules/components/LeadSelect";

import { useCreateReminder, useUpdateReminder } from "../hooks/use-reminders";
import type { CreateReminderFormData } from "../schema/reminder-schema";
import { createReminderSchema } from "../schema/reminder-schema";

interface NewReminderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingReminder?: ReminderUI;
  // Optional: Preselect a lead when creating reminder from lead details page
  preselectedLeadId?: string;
  preselectedLeadName?: string;
}

export const NewReminderDialog = function ({
  open,
  onOpenChange,
  editingReminder,
  preselectedLeadId,
  preselectedLeadName,
}: NewReminderDialogProps) {
  const [associations, setAssociations] = useState<
    {
      entityType: string;
      entityId: string;
      notes?: string;
    }[]
  >([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState<string>("");
  const timeListRef = useRef<HTMLDivElement>(null);

  // Get current time rounded to next 15-minute slot
  const getDefaultTime = () => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = Math.ceil(now.getMinutes() / 15) * 15;
    const adjustedHours = minutes === 60 ? hours + 1 : hours;
    const adjustedMinutes = minutes === 60 ? 0 : minutes;
    const finalHours = adjustedHours >= 24 ? 23 : adjustedHours;
    const finalMinutes = adjustedHours >= 24 ? 45 : adjustedMinutes;
    return `${String(finalHours).padStart(2, '0')}:${String(finalMinutes).padStart(2, '0')}`;
  };

  // Get current time slot value for scrolling
  const getCurrentTimeSlotValue = useCallback(() => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = Math.floor(now.getMinutes() / 15) * 15;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }, []);

  // Scroll to current time slot when popover opens
  const scrollToCurrentTime = useCallback(() => {
    setTimeout(() => {
      if (timeListRef.current) {
        const currentSlotValue = getCurrentTimeSlotValue();
        const targetButton = timeListRef.current.querySelector(`[data-time="${currentSlotValue}"]`);
        if (targetButton) {
          targetButton.scrollIntoView({ block: 'center', behavior: 'auto' });
        }
      }
    }, 50);
  }, [getCurrentTimeSlotValue]);

  const createReminderMutation = useCreateReminder();
  const updateReminderMutation = useUpdateReminder();

  const form = useForm<CreateReminderFormData>({
    resolver: zodResolver(createReminderSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      title: "",
      date: format(new Date(), "yyyy-MM-dd"),
      text: "",
      associations: [],
      type: "task",
      priority: "medium",
      dueDate: new Date(),
      isCompleted: false,
      isRecurring: false,
      recurringPattern: "weekly",
      reminderTime: (() => {
        const defaultTimeStr = getDefaultTime();
        const [hours, minutes] = defaultTimeStr.split(':').map(Number);
        const defaultTime = new Date();
        defaultTime.setHours(hours, minutes, 0, 0);
        return defaultTime;
      })(),
      associatedClientId: "",
      associatedClientName: "",
      notes: "",
    },
  });

  const {
    control,
    formState: { isValid },
    reset,
    handleSubmit,
    watch,
    setValue,
    trigger,
  } = form;

  const isRecurring = watch("isRecurring");

  // Form population and reset effect
  useEffect(() => {
    if (open) {
      setIsSubmitting(false); // Reset submission state when opening
      if (editingReminder) {
        populateFormWithEditingData();
      } else {
        resetFormToDefaults();
      }
      // Remove manual trigger - let onChange mode handle validation
      // setTimeout(() => trigger(), 100);
    } else {
      // Reset submission state when closing
      setIsSubmitting(false);
    }
  }, [open, editingReminder, setValue, trigger]);

  const populateFormWithEditingData = () => {
    if (!editingReminder) return;

    const existingDate = editingReminder.date || editingReminder.dueDate;

    setValue("title", editingReminder.title);
    setValue(
      "date",
      typeof existingDate === "string"
        ? existingDate
        : format(new Date(existingDate), "yyyy-MM-dd")
    );
    setValue("text", editingReminder.text || editingReminder.description || "");
    setValue("type", editingReminder.type || "task");
    setValue("priority", editingReminder.priority);
    setValue("isCompleted", editingReminder.isCompleted ?? false);
    setValue("isRecurring", editingReminder.isRecurring ?? false);
    setValue("recurringPattern", editingReminder.recurringPattern || "weekly");
    // Set reminderTime with a default if undefined
    if (editingReminder.reminderTime) {
      // Handle both Date object and string (HH:mm:ss format from backend)
      let reminderTimeValue: Date;
      if (typeof editingReminder.reminderTime === 'string') {
        // Parse time string (HH:mm:ss or HH:mm)
        const [hours, minutes, seconds] = editingReminder.reminderTime.split(':').map(Number);
        reminderTimeValue = new Date();
        reminderTimeValue.setHours(hours, minutes, seconds || 0, 0);
      } else {
        reminderTimeValue = editingReminder.reminderTime;
      }
      setValue("reminderTime", reminderTimeValue);
    } else {
      const defaultTimeStr = getDefaultTime();
      const [hours, minutes] = defaultTimeStr.split(':').map(Number);
      const defaultTime = new Date();
      defaultTime.setHours(hours, minutes, 0, 0);
      setValue("reminderTime", defaultTime);
    }
    setValue("associatedClientId", editingReminder.associatedClientId || "");
    setValue(
      "associatedClientName",
      editingReminder.associatedClientName || ""
    );
    setValue("notes", editingReminder.notes || "");

    // Set selected lead ID if editing a reminder with an associated lead
    if (editingReminder.associatedClientId) {
      setSelectedLeadId(editingReminder.associatedClientId);
    }

    // Convert date to Date object for dueDate field (avoid UTC conversion)
    if (existingDate) {
      let dateObj: Date;
      if (typeof existingDate === "string") {
        // Parse YYYY-MM-DD as local date, not UTC
        const [year, month, day] = existingDate.split('-').map(Number);
        dateObj = new Date(year, month - 1, day, 12, 0, 0, 0); // Set to noon local time
      } else {
        dateObj = new Date(existingDate);
      }
      // Only set the date if it's valid
      if (!isNaN(dateObj.getTime())) {
        setValue("dueDate", dateObj);
      }
    }

    // Build associations array, ensuring Lead association is included if associatedClientId exists
    const existingAssociations = (editingReminder.associations || [])
      .filter((assoc) => assoc.entityType !== "UI_METADATA")
      .map((assoc) => ({
        entityType: assoc.entityType,
        entityId: assoc.entityId,
        notes: assoc.notes,
      }));

    // Check if there's already a Lead association in the array
    const hasLeadAssociation = existingAssociations.some(
      (assoc) => assoc.entityType?.toLowerCase() === "lead"
    );

    // If associatedClientId is set but no Lead association exists, add one
    if (editingReminder.associatedClientId && !hasLeadAssociation) {
      existingAssociations.push({
        entityType: "Lead",
        entityId: editingReminder.associatedClientId,
        notes: editingReminder.associatedClientName
          ? `Associated with ${editingReminder.associatedClientName}`
          : "",
      });
    }

    setAssociations(existingAssociations);
  };

  const resetFormToDefaults = () => {
    const today = new Date();
    const todayString = format(today, "yyyy-MM-dd");
    const defaultTimeStr = getDefaultTime();
    const [defaultHours, defaultMinutes] = defaultTimeStr.split(':').map(Number);
    const defaultTime = new Date();
    defaultTime.setHours(defaultHours, defaultMinutes, 0, 0);

    // Use reset() instead of individual setValue calls to properly clear errors
    // If a lead is preselected (from lead details page), auto-fill the association
    reset({
      title: "",
      date: todayString,
      text: "",
      type: "task",
      priority: "medium",
      dueDate: today,
      isCompleted: false,
      isRecurring: false,
      recurringPattern: "weekly",
      reminderTime: defaultTime,
      associatedClientId: preselectedLeadId || "",
      associatedClientName: preselectedLeadName || "",
      notes: "",
      associations: [],
    });

    // If lead is preselected, set up associations
    if (preselectedLeadId) {
      setSelectedLeadId(preselectedLeadId);
      setAssociations([{
        entityType: "Lead",
        entityId: preselectedLeadId,
        notes: preselectedLeadName ? `Associated with ${preselectedLeadName}` : "",
      }]);
    } else {
      setAssociations([]);
      setSelectedLeadId("");
    }
  };

  const handleLeadSelect = (leadId: string, leadName?: string) => {
    setSelectedLeadId(leadId);
    setValue("associatedClientId", leadId);
    setValue("associatedClientName", leadName || "");

    // Update associations array to include the lead
    const leadAssociation = {
      entityType: "Lead",
      entityId: leadId,
      notes: leadName ? `Associated with ${leadName}` : "",
    };

    // Remove any existing Lead associations and add the new one
    const otherAssociations = associations.filter(a => a.entityType !== "Lead");
    setAssociations([...otherAssociations, leadAssociation]);
  };

  const onSubmit = (data: CreateReminderFormData) => {
    // Prevent duplicate submissions
    if (isLoading || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    if (!data.dueDate) {
      setIsSubmitting(false);
      return;
    }

    // Create the final reminder date/time in local timezone
    // Start with the selected date (which is already in local timezone at noon)
    const selectedDate = new Date(data.dueDate);

    // If reminder time is set, use it; otherwise use 9 AM
    if (data.reminderTime) {
      const timeHours = data.reminderTime.getHours();
      const timeMinutes = data.reminderTime.getMinutes();
      selectedDate.setHours(timeHours, timeMinutes, 0, 0);
    } else {
      selectedDate.setHours(9, 0, 0, 0);
    }

    // Check if the final date/time is in the past (warn but allow)
    const now = new Date();
    if (selectedDate <= now) {
      toast({
        title: "Past Date Warning",
        description: "Reminder date and time is in the past. This is allowed but may not be useful.",
        variant: "default",
      });
      // Don't return - allow past dates for historical reminders
    }

    // Format date as YYYY-MM-DD in local timezone (not UTC)
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const ymdDate = `${year}-${month}-${day}`;

    // Convert reminderTime to HH:mm:ss string format for the backend
    const reminderTimeStr = data.reminderTime ? (() => {
      const hours = String(data.reminderTime.getHours()).padStart(2, '0');
      const minutes = String(data.reminderTime.getMinutes()).padStart(2, '0');
      const seconds = String(data.reminderTime.getSeconds()).padStart(2, '0');
      return `${hours}:${minutes}:${seconds}`;
    })() : '09:00:00';

    const reminderUIData = {
      id: editingReminder?.id,
      title: data.title,
      date: ymdDate,
      // Send dueDate as ISO string in local timezone format (without Z suffix)
      // This prevents UTC conversion issues when the date crosses midnight
      dueDate: ymdDate + 'T' + String(selectedDate.getHours()).padStart(2, '0') + ':' + String(selectedDate.getMinutes()).padStart(2, '0') + ':00',
      text: data.text,
      description: data.text,
      type: data.type,
      priority: data.priority,
      isRecurring: data.isRecurring,
      recurringPattern: data.recurringPattern,
      reminderTime: reminderTimeStr, // Send as HH:mm:ss string for backend compatibility
      associatedClientId: data.associatedClientId,
      associatedClientName: data.associatedClientName,
      notes: data.notes,
      // Add UI_METADATA association for proper backend handling
      associations: [
        ...associations.map((assoc) => ({
          entityType: assoc.entityType,
          entityId: assoc.entityId,
          notes: assoc.notes || "",
          metadata: {},
        })),
        {
          entityType: "UI_METADATA",
          entityId: (() => {
            try {
              if (typeof crypto !== 'undefined' && crypto.randomUUID) {
                return crypto.randomUUID();
              }
              // Fallback for older browsers
              return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
                const r = Math.random() * 16 | 0;
                const v = c === 'x' ? r : (r & 0x3 | 0x8);
                return v.toString(16);
              });
            } catch {
              return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
            }
          })(),
          metadata: {
            description: data.text,
            type: data.type,
            priority: data.priority,
            isRecurring: data.isRecurring,
            recurringPattern: data.recurringPattern,
            reminderTime: data.reminderTime ? (() => {
              const hours = String(data.reminderTime.getHours()).padStart(2, '0');
              const minutes = String(data.reminderTime.getMinutes()).padStart(2, '0');
              const seconds = String(data.reminderTime.getSeconds()).padStart(2, '0');
              return `${hours}:${minutes}:${seconds}`;
            })() : '09:00:00',
            // Include lead association data for display when fetching reminders
            ...(data.associatedClientId && { associatedClientId: data.associatedClientId }),
            ...(data.associatedClientName && { associatedClientName: data.associatedClientName }),
            ...(data.notes && { notes: data.notes }),
          },
          notes: "",
        },
      ],
      // Add remindAt field for backend compatibility - send as local datetime string
      // Format: YYYY-MM-DDTHH:MM:SS (without Z suffix to avoid UTC conversion)
      remindAt: ymdDate + 'T' + String(selectedDate.getHours()).padStart(2, '0') + ':' + String(selectedDate.getMinutes()).padStart(2, '0') + ':00',
    };

    const onSuccess = async () => {
      setIsSubmitting(false);
      toast({
        title: editingReminder ? "Reminder Updated" : "Reminder Created",
        description: editingReminder
          ? "Reminder was successfully updated."
          : "New reminder has been created.",
      });

      // Sync reminder notes to lead notes (if reminder has a lead association + notes)
      if (!editingReminder && data.notes && data.associatedClientId) {
        try {
          const { getApiUrl } = await import("@/app/_module/services/config");
          const { endpoint } = await import("@/app/_module/services/endpoints");
          const { default: api } = await import("@/app/_module/services/api");
          await api.post({
            url: getApiUrl(endpoint.leads.getLeadNotes(data.associatedClientId)),
            body: {
              content: `📋 Reminder: ${data.notes}`,
              type: 'reminder',
            },
            auth: true,
          });
        } catch {
          // Non-critical — reminder was still created
        }
      }

      onOpenChange(false);

      // Reset form and associations after successful submission
      if (!editingReminder) {
        reset();
        setAssociations([]);
      }
    };

    const onError = (error: unknown) => {
      setIsSubmitting(false);
      const errorMessage = error instanceof Error ? error.message : "Something went wrong. Please try again.";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    };

    // Safety timeout to prevent getting stuck in submitting state
    const safetyTimeout = setTimeout(() => {
      if (isSubmitting) {

        setIsSubmitting(false);
      }
    }, 30000); // 30 second timeout

    try {
      if (editingReminder) {
        updateReminderMutation.mutate(
          {
            reminderId: editingReminder.id,
            reminderData: reminderUIData,
            statusOnly: false,
          },
          {
            onSuccess: () => {
              clearTimeout(safetyTimeout);
              onSuccess();
            },
            onError: (error) => {
              clearTimeout(safetyTimeout);
              onError(error);
            }
          }
        );
      } else {
        createReminderMutation.mutate(reminderUIData, {
          onSuccess: () => {
            clearTimeout(safetyTimeout);
            onSuccess();
          },
          onError: (error) => {
            clearTimeout(safetyTimeout);
            onError(error);
          }
        });
      }
    } catch (error) {
      clearTimeout(safetyTimeout);

      setIsSubmitting(false);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCancel = () => {
    setIsSubmitting(false);
    onOpenChange(false);
    if (!editingReminder) {
      reset();
      setAssociations([]);
    }
  };

  const addAssociation = () => {
    setAssociations([...associations, { entityType: "", entityId: "" }]);
  };

  const removeAssociation = (index: number) => {
    setAssociations(associations.filter((_, i) => i !== index));
  };

  const updateAssociation = (index: number, field: string, value: string) => {
    const updated = [...associations];
    updated[index] = { ...updated[index], [field]: value };
    setAssociations(updated);
  };

  const isLoading =
    createReminderMutation.isPending || updateReminderMutation.isPending || isSubmitting;

  return (
    <>
      {!!open && (
        <div
          className="fixed inset-0 bg-black/50 z-40 transition-opacity"
          onClick={(e) => {
            e.stopPropagation();
            onOpenChange(false);
          }}
        />
      )}
      <Dialog modal={false} open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent
        size="2xl"
        className="max-h-[90vh] overflow-y-auto z-50"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
          <DialogHeader>
            <DialogTitle>
              {editingReminder ? "Edit Reminder" : "New Reminder"}
            </DialogTitle>
            <DialogDescription>
              {editingReminder ? "Update the reminder details below" : "Create a new reminder"}
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form 
              className="space-y-4" 
              onSubmit={(e) => {
                e.preventDefault();
                if (!isLoading && !isSubmitting) {
                  handleSubmit(onSubmit)(e);
                }
              }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={control}
                  name="title"
                  render={({ field, fieldState }) => (
                    <Input
                      required
                      error={fieldState.error}
                      label="Title"
                      placeholder="Enter reminder title"
                      {...field}
                    />
                  )}
                />

                <FormField
                  control={control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Type *</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select reminder type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="appointment">
                            Appointment
                          </SelectItem>
                          <SelectItem value="follow-up">Follow-up</SelectItem>
                          <SelectItem value="task">Task</SelectItem>
                          <SelectItem value="deadline">Deadline</SelectItem>
                          <SelectItem value="birthday">Birthday</SelectItem>
                          <SelectItem value="custom">Custom</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={control}
                name="text"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Text *</FormLabel>
                    <FormControl>
                      <Textarea
                        className="resize-none"
                        placeholder="Enter reminder text"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField
                  control={control}
                  name="dueDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Due Date *</FormLabel>
                      <Popover>
                        <FormControl>
                          <div className="relative">
                            <Input
                              type="date"
                              value={field.value ? format(field.value, "yyyy-MM-dd") : ""}
                              onChange={(e) => {
                                if (e.target.value) {
                                  const [year, month, day] = e.target.value.split('-');
                                  const localDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0, 0);
                                  field.onChange(localDate);
                                } else {
                                  field.onChange(null);
                                }
                              }}
                              className="w-full pr-10"
                              min={format(new Date(), "yyyy-MM-dd")}
                            />
                            <PopoverTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                              >
                                <CalendarIcon className="h-4 w-4 text-gray-400" />
                              </Button>
                            </PopoverTrigger>
                          </div>
                        </FormControl>
                        <PopoverContent align="start" className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={(date) => {
                              if (date) {
                                // Create date at noon in local timezone to avoid DST issues
                                const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0);
                                field.onChange(localDate);
                              }
                            }}
                            disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="priority"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Priority *</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select priority" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="urgent">Urgent</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="low">Low</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="reminderTime"
                  render={({ field }) => {
                    // Helper functions for time comparison
                    const getCurrentTime = () => {
                      const now = new Date();
                      return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
                    };

                    const getTodayDate = () => {
                      const now = new Date();
                      return format(now, "yyyy-MM-dd");
                    };

                    const selectedDueDate = watch("dueDate");
                    const selectedDateStr = selectedDueDate ? format(selectedDueDate, "yyyy-MM-dd") : "";

                    // Generate time slots (every 15 minutes)
                    const generateTimeSlots = () => {
                      const slots: { value: string; label: string; disabled: boolean }[] = [];
                      const currentTime = getCurrentTime();
                      const todayDate = getTodayDate();
                      const isTodaySelected = selectedDateStr === todayDate;

                      for (let hour = 0; hour < 24; hour++) {
                        for (let minute = 0; minute < 60; minute += 15) {
                          const hourStr = String(hour).padStart(2, '0');
                          const minStr = String(minute).padStart(2, '0');
                          const value = `${hourStr}:${minStr}`;
                          const hour12 = hour % 12 || 12;
                          const ampm = hour < 12 ? 'AM' : 'PM';
                          const label = `${hour12}:${minStr} ${ampm}`;

                          // Disable past times only if today is selected
                          const disabled = isTodaySelected && value < currentTime;

                          slots.push({ value, label, disabled });
                        }
                      }
                      return slots;
                    };
                    const timeSlots = generateTimeSlots();

                    // Format time to 12-hour display
                    const formatTimeDisplay = (date: Date | undefined) => {
                      if (!date) return 'Select time';
                      const hours = date.getHours();
                      const minutes = date.getMinutes();
                      const hour12 = hours % 12 || 12;
                      const ampm = hours < 12 ? 'AM' : 'PM';
                      return `${hour12}:${String(minutes).padStart(2, '0')} ${ampm}`;
                    };

                    // Get current value as HH:mm string
                    const currentValue = field.value ? format(field.value, "HH:mm") : "";

                    return (
                      <FormItem>
                        <FormLabel>Reminder Time *</FormLabel>
                        <Popover onOpenChange={(open) => {
                          if (open) scrollToCurrentTime();
                        }}>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                className={cn(
                                  "w-full justify-start text-left font-normal",
                                  !field.value && "text-muted-foreground"
                                )}
                              >
                                {formatTimeDisplay(field.value)}
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-[280px] p-0 z-[60]" align="start">
                            {/* Manual time input at top */}
                            <div className="p-3 border-b">
                              <p className="text-xs text-muted-foreground mb-2">Type custom time:</p>
                              <Input
                                type="time"
                                value={currentValue}
                                onChange={(e) => {
                                  if (e.target.value) {
                                    const [hours, minutes] = e.target.value.split(":").map(Number);
                                    const dueDate = watch("dueDate") || new Date();
                                    const reminderTime = new Date(dueDate);
                                    reminderTime.setHours(hours, minutes, 0, 0);
                                    field.onChange(reminderTime);
                                  }
                                }}
                                className="w-full"
                              />
                            </div>
                            {/* Quick select 15-min intervals */}
                            <div className="p-2">
                              <p className="text-xs text-muted-foreground mb-2 px-1">Or select:</p>
                              <div ref={timeListRef} className="grid grid-cols-4 gap-1 max-h-[200px] overflow-y-auto">
                                {timeSlots.map((slot) => (
                                  <Button
                                    key={slot.value}
                                    type="button"
                                    data-time={slot.value}
                                    variant={currentValue === slot.value ? "default" : "ghost"}
                                    size="sm"
                                    disabled={slot.disabled}
                                    className={cn(
                                      "text-xs h-8",
                                      slot.disabled && "text-muted-foreground line-through opacity-50",
                                      currentValue === slot.value && "bg-primary text-primary-foreground"
                                    )}
                                    onClick={() => {
                                      const [hours, minutes] = slot.value.split(":").map(Number);
                                      const dueDate = watch("dueDate") || new Date();
                                      const reminderTime = new Date(dueDate);
                                      reminderTime.setHours(hours, minutes, 0, 0);
                                      field.onChange(reminderTime);
                                    }}
                                  >
                                    {slot.label}
                                  </Button>
                                ))}
                              </div>
                            </div>
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              </div>

              <div className="space-y-4">
                <FormField
                  control={control}
                  name="isRecurring"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>Recurring reminder</FormLabel>
                      </div>
                    </FormItem>
                  )}
                />

                {!!isRecurring && (
                  <FormField
                    control={control}
                    name="recurringPattern"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Repeat *</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select repeat pattern" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="daily">Daily</SelectItem>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                            <SelectItem value="yearly">Yearly</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>


              <FormField
                control={control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes</FormLabel>
                    <FormControl>
                      <Textarea
                        className="resize-none"
                        placeholder="Additional notes..."
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  disabled={isLoading}
                  onClick={handleCancel}
                  type="button"
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button 
                  disabled={!isValid || isLoading} 
                  type="submit"
                  className={isLoading ? "opacity-50 cursor-not-allowed" : ""}
                >
                  {isLoading
                    ? "Saving..."
                    : editingReminder
                      ? "Update"
                      : "Create"}{" "}
                  Reminder
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </ResponsiveDialogContent>
      </Dialog>
    </>
  );
};
